import { bunja } from 'bunja';
import { useBunja } from 'bunja/react';
import { atom, useAtomValue } from 'jotai';
import { atomFamily } from 'jotai-family';
import { selectAtom } from 'jotai/utils';
import { useEffect, useRef, useState } from 'react';

import { OrderTypeEnum } from '@flipster/apis/types';
import { FormattedMessage } from '@flipster/i18n';
import { ordersBunja } from '@flipster/r230522/original/order';
import { Checkbox } from '@flipster/react/design-system/checkbox';
import Num from '@flipster/utils/num';
import segment, { ChartEvent } from '@flipster/utils/segment';

import { useTradingViewDataStorage } from '../../../misc/data-manager';
import { symbolNameToInfo } from '../../../misc/helper';
import { TvEventHandler } from '../../../trading-view';
import { useTvHeaderContext } from '../../index';
import OrderLineHelper from './line-helpers/order-line-helper';

const triggerOrdersBunja = bunja(() => {
  const { pendingPositionsAtom } = bunja.use(ordersBunja);

  const symbolTriggerOrdersAtomFamily = atomFamily((symbol: string) =>
    selectAtom(
      atom((get) => {
        const pendingPositions = get(pendingPositionsAtom);
        return pendingPositions?.[symbol]?.filter((position) => position.orderType !== OrderTypeEnum['Limit']) ?? [];
      }),
      (v) => v,
      (a, b) => {
        if (a === b) return true;
        if (a.length !== b.length) return false;
        return a.every((orderA, i) => {
          const orderB = b[i];
          return (
            orderA.orderId === orderB.orderId &&
            orderA.triggerPrice === orderB.triggerPrice &&
            orderA.qty === orderB.qty &&
            orderA.side === orderB.side &&
            orderA.slot === orderB.slot
          );
        });
      },
    ),
  );

  return { symbolTriggerOrdersAtomFamily };
});

export const TriggerOrdersPlugin = () => {
  const tv = useTvHeaderContext();
  const lineHelperRef = useRef<OrderLineHelper>(null);
  const [checked, setChecked] = useTradingViewDataStorage({ key: 'triggerOrderVisible' });
  const [symbol, setSymbol] = useState<string>('');
  const { symbolTriggerOrdersAtomFamily } = useBunja(triggerOrdersBunja);
  const { symbol: symbolWithoutPriceType } = symbolNameToInfo(symbol);
  const symbolPositions = useAtomValue(symbolTriggerOrdersAtomFamily(symbolWithoutPriceType));

  useEffect(() => {
    if (!lineHelperRef.current)
      lineHelperRef.current = new OrderLineHelper(tv, 'Trigger', (order) => Num(order.triggerPrice).toNumber());
    const handler: TvEventHandler<'onFirstDataLoaded'> = ({ symbol: newSymbol, symbolChanged }) => {
      if (!symbolChanged) return;
      lineHelperRef.current?.clean();
      setSymbol(newSymbol);
    };
    return tv.on('onFirstDataLoaded', handler);
  }, [tv, symbol]);

  useEffect(() => {
    if (!checked.value || !symbolPositions?.length) {
      lineHelperRef.current?.clean();
      return;
    }
    lineHelperRef.current?.draw(symbol, symbolPositions);
  }, [checked.value, symbol, symbolPositions]);

  useEffect(() => {
    return () => {
      lineHelperRef.current?.clean();
    };
  }, []);

  return (
    <Checkbox
      checked={checked.value}
      onChange={(e) => {
        setChecked(e.currentTarget.checked);
        segment.track(ChartEvent.ChartEventToggleClick, {
          event_name: 'Trigger orders',
          action: e.currentTarget.checked ? 'turn_on' : 'turn_off',
        });
      }}
    >
      <FormattedMessage id='Trade.r260616.Trigger_orders' />
    </Checkbox>
  );
};
